import type { Amount } from '@skippay/core/@types/amount';
import { toFloat } from '@skippay/core/utils/amountUtils';
import { Paginated } from '@skippay/core/utils/queryUtils';
import { Maybe } from '@skippay/core/utils/types';

import { toIsoDate } from 'jarabot/utils';
import CoinsOverviewQuery from 'queries/CoinsOverviewQuery.graphql';
import MyContractCardsQuery from 'queries/MyContractCardsQuery.graphql';
import WalletQuery from 'sections/cashback/WalletQuery.graphql';

import { CardInfo, CashbackInfo, CoinsInfo } from '../../types';
import { DomainSource } from '../types';

interface CoinsData {
	coinsOverview?: {
		coinBalance?: Maybe<Amount>;
		earliestExpiration?: { date?: Maybe<string>; sum?: Maybe<Amount> } | null;
	} | null;
}

export const coinsSource: DomainSource<CoinsData> = {
	domains: ['coins'],
	query: CoinsOverviewQuery,
	toModel: (data) => {
		const overview = data.coinsOverview;
		if (!overview) return {};
		const expiration = overview.earliestExpiration;
		const coins: CoinsInfo = {
			balance: overview.coinBalance ? toFloat(overview.coinBalance) : undefined,
			nearestExpiration: expiration
				? {
						date: toIsoDate(expiration.date),
						amount: expiration.sum ? toFloat(expiration.sum) : undefined,
					}
				: undefined,
		};
		return { coins };
	},
};

export const cashbackSource: DomainSource<{
	myCashbackWallet?: { availableCashback?: Maybe<Amount> } | null;
}> = {
	domains: ['cashback'],
	query: WalletQuery,
	toModel: (data) => {
		const wallet = data.myCashbackWallet;
		if (!wallet) return {};
		const cashback: CashbackInfo = {
			available: wallet.availableCashback ? toFloat(wallet.availableCashback) : undefined,
		};
		return { cashback };
	},
};

interface CardRow {
	state?: Maybe<string>;
	stateReason?: Maybe<string>;
}

export const cardSource: DomainSource<{ myContractCards?: Paginated<CardRow> }> = {
	domains: ['card'],
	query: MyContractCardsQuery,
	toModel: (data) => {
		const card = data.myContractCards?.data?.[0];
		if (!card) return {};
		const cardInfo: CardInfo = {
			state: card.state ?? undefined,
			stateReason: card.stateReason ?? undefined,
		};
		return { card: cardInfo };
	},
};
